import React from "react";
// React Components
import { Button } from "react-bootstrap";
// Logos
import LogoUulm from "../../img/Logo_uulm.png";
import UKF_LOGO from "../../img/UKF_LOGO.png";

import { Link, useLocation } from "react-router";
import { SideBarItem, SubmenuItem } from "../../types/sidebarTypes";

interface BasicSidebarProps {
  sidebarItems: (SideBarItem | "---")[]; // Sidebar item or horizontal line
}

const BasicSidebar = ({ sidebarItems }: BasicSidebarProps) => {
  /**
   * creates a sidebar item
   * @param sideBarItem all Information needed (refer to typeDefinition)
   * @returns a sidebar item
   */

  const location = useLocation();

  const renderSideBarItem = (
    sideBarItem: SideBarItem,
    extraClassName?: string,
  ) => {
    const liClassName = `nav-item w-100 pb-1 ${extraClassName ?? ""}`.trim();
    return (
      <li className={liClassName}>
        <Button
          title={sideBarItem.title}
          aria-label={sideBarItem.title}
          id={sideBarItem.buttonId}
          className={`${sideBarItem.activeButton ? "border border-1 border-light" : ""} bg-transparent border-0 align-middle px-0 w-100 d-flex font-french-gray`}
          onClick={() => sideBarItem.onClickFunction()}
          aria-expanded={sideBarItem.ariaExpand}
          aria-controls={sideBarItem.ariaControl}
          data-bs-toggle="collapse"
          data-bs-target={sideBarItem.dataTarget}
        >
          {/* Icon + Text container */}
          <span className="d-flex w-100 overflow-hidden align-items-center">
            {/* Icon left */}
            {renderSideBarItemIcon(sideBarItem)}

            {/* Text */}
            <span className="ms-1 d-none d-md-inline text-truncate">
              {sideBarItem.title}
            </span>

            {/* Icon right of text */}
            {!sideBarItem.hideChevron && renderChevron(sideBarItem)}
          </span>
        </Button>

        {/* Submenu */}
        {sideBarItem.subMenu &&
          renderInformationSubMenu(
            sideBarItem.subMenu,
            sideBarItem.activeButton,
          )}
      </li>
    );
  };

  const renderChevron = (sideBarItem: SideBarItem) => {
    const hasSubmenu = sideBarItem.subMenu && sideBarItem.subMenu.length > 0;
    const subMenuOpen = sideBarItem.activeButton;
    const closedSubMenu = (
      <span className="material-symbols-rounded ms-auto text-end overflow-hidden d-none d-sm-block">
        chevron_right
      </span>
    );
    const openSubMenu = (
      <span className="material-symbols-rounded ms-auto text-end overflow-hidden d-none d-sm-block">
        keyboard_arrow_down
      </span>
    );
    let returnHtml = closedSubMenu;

    if (hasSubmenu && subMenuOpen) returnHtml = openSubMenu;
    return returnHtml;
  };

  /**
   * renders the icon for pc and mobile view
   */
  const renderSideBarItemIcon = (sideBarItem: SideBarItem) => {
    // calculate if icon should be shown as active
    let activeSideItem = false;
    if (sideBarItem.subMenu) {
      // test if submenus are currently active hrefpaths
      for (const subMenuItem of sideBarItem.subMenu) {
        if (subMenuItem.redirectLink === location.pathname)
          activeSideItem = true;
      }
    } else {
      // no submenu => onClick makes it automatically true
      activeSideItem = true;
    }

    activeSideItem = sideBarItem.activeButton && activeSideItem;
    // Put the icon design together
    const generalStyle = `${activeSideItem ? "font-light-sea-green" : ""} d-flex align-items-center `;
    const mobileViewStyle = "mx-auto d-block d-sm-none";
    const pcViewStyle = `ps-1 d-none d-sm-inline me-3 `;

    const iconContent = (
      <>
        {React.isValidElement(sideBarItem.imgSrc) ? (
          sideBarItem.imgSrc
        ) : typeof sideBarItem.imgSrc === "string" &&
          sideBarItem.imgSrc !== "" ? (
          <img
            className="rounded-circle border border-2 border-white"
            src={sideBarItem.imgSrc}
            width="25px"
            alt={sideBarItem.title}
          />
        ) : null}
      </>
    );

    return (
      <>
        <i className={`${generalStyle} ${mobileViewStyle}`}>{iconContent}</i>
        <i className={`${generalStyle} ${pcViewStyle}`}>{iconContent}</i>
      </>
    );
  };

  /**
   * renderes a submenu of an sidebar item
   * @param items name & href of items
   * @param collapseVar when should the submenu be displayed
   * @returns a submenu for the sidebar
   */
  const renderInformationSubMenu = (items: SubmenuItem[], collapseVar) => (
    <ul
      id="documentation"
      data-parent="#sidebar-menu"
      className={`${collapseVar ? "show" : "collapse"}`}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {renderInformationSubMenuItem(
            item.title,
            item.redirectLink,
            item.onClickFunction,
          )}
        </React.Fragment>
      ))}
    </ul>
  );

  /**
   * renderes a submenu item
   * @param itemText text/ title of item
   * @param redirectLink where click on item should redirect to
   * @returns submenu item
   */
  const renderInformationSubMenuItem = (
    itemText: string,
    redirectLink: string,
    onClickFunction: (() => void) | undefined,
  ) => (
    <li>
      <Link
        className="w-100 pb-1 text-truncate d-block link-underline link-underline-opacity-0 font-light-sea-green"
        to={{
          pathname: redirectLink,
        }}
        onClick={onClickFunction}
      >
        {itemText}
      </Link>
    </li>
  );

  /**
   * renderes an logo on the sidebar
   * @param id        id of link-element
   * @param href      where click on logo should redirect to
   * @param imgSrc    path to image
   * @param imgAlt    alternative text for slow internet/ blind persons
   * @param imgTitle  title of image
   * @returns
   */
  const renderLogo = (
    id: string,
    href: string,
    imgSrc: string,
    imgSize: string,
    imgAlt: string,
    imgTitle: string,
  ) => (
    <a
      id={id}
      className="d-none d-sm-flex justify-content-center w-100 p-2"
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      <img
        src={imgSrc}
        style={{ maxWidth: imgSize }}
        alt={imgAlt}
        title={imgTitle}
      />
    </a>
  );

  return (
    <>
      {/* set width of sidebar */}
      <div
        className="col-auto col-2 col-md-2 col-xl-2 px-sm-2 px-2 pt-2 background-sidebar d-flex flex-column"
        style={{
          position: "sticky",
          top: 0,
          height: "100vh",
          zIndex: 1020,
        }}
      >
        {/* set content alignment */}
        <div className="d-flex flex-column align-items-center min-vh-100 gap-3">
          <ul className="nav nav-pills flex-column align-items-center align-items-sm-start pb-4 h-auto w-100">
            {sidebarItems.map((item: SideBarItem | "---", index: number) => {
              let returnValue: React.ReactElement;
              if (item === "---") {
                // sidebar-line
                returnValue = (
                  <li>
                    <hr className="border-top border-2 my-1 w-100 border-alice-blue" />
                  </li>
                );
              } else {
                // sidebar-content
                returnValue = renderSideBarItem(
                  item,
                  item.hiddenOnMobile ? "d-none d-sm-inline" : "",
                );
              }
              return <React.Fragment key={index}>{returnValue}</React.Fragment>;
            })}
          </ul>

          {/* two logos in order */}
          <div
            className="mt-auto d-flex flex-column align-items-center w-100"
            style={{ paddingBottom: "8rem" }}
          >
            {renderLogo(
              "sidebar-logo-freiburg",
              "https://www.uniklinik-freiburg.de/psychosomatik/patientinnen-info/sektion-systemische-gesundheitsforschung/laufende-projekte/projektseiten/sysdok-entwicklung-implementierung-und-evaluation-eines-dokumentations-und-forschungssystems-fuer-ambulante-systemische-psychotherapie.html",
              UKF_LOGO,
              "100%",
              "Logo Universitätsklinikum Freiburg: Öffnet neuen Tab: Universitätsklinikumwebseite",
              "Öffnet neuen Tab: Universisätsklinikumwebseite",
            )}
            {renderLogo(
              "sidebar-logo-ulm",
              "https://www.uni-ulm.de/in/abt-klinische-psychologie-und-psychotherapie/",
              LogoUulm,
              "80%",
              "Logo Universität Ulm: Öffnet neuen Tab: Universitätswebseite der Abteilung klinische Psychologie und Psychotherapie",
              "Öffnet neuen Tab: Universisätswebseite der Abteilung klinische Psychologie und Psychotherapie",
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default BasicSidebar;
